import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoMedia } = this.dataset;

    // Prevent video auto-downloading on incorrect media widths
    if (!videoMedia) {
      this.#load();
      return;
    }

    this.mediaQuery = matchMedia(videoMedia);
    this.mediaQuery.addEventListener('change', this.loadWhenMatched);
    this.loadWhenMatched();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.mediaQuery?.removeEventListener('change', this.loadWhenMatched);
  }

  loadWhenMatched = () => {
    if (this.mediaQuery?.matches) this.#load();
  };

  #load() {
    if (this.hasLoaded) return;
    this.hasLoaded = true;

    const { videoSources, videoElement } = this.refs;

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      videoElement.removeAttribute('autoplay');
    }

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
